import React, { useEffect, useState } from 'react';
import { Download, Plus, Search, Upload } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Button } from '../../../shared/components/Button';
import { Can } from '../../../shared/components/Can';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Pagination } from '../../../shared/components/Pagination';
import { Skeleton } from '../../../shared/components/Skeleton';
import { TextInput } from '../../../shared/components/TextInput';
import { FEATURES } from '../../../shared/lib/permissions';
import { obterUrlCurriculo, type Candidate } from '../api';
import { useCandidatos, useDocumentosCandidato, useEnviarCurriculo } from '../hooks';
import { CandidatoModal } from '../components/CandidatoModal';
import { formatCurrencyBRL, formatDateBR } from '../components/format';

const PAGE_SIZE = 25;

/**
 * Cadastro e busca de candidatos.
 *
 * O CPF chega mascarado da API para quem não tem
 * `recrutamento.dados-sensiveis` — a tela mostra o que recebeu, sem
 * tentar remontar o valor. É por isso que `cpfMasked` vem no payload:
 * para a interface poder dizer *por que* o dado está encoberto.
 */
export default function CandidatosPage(): React.ReactElement {
  const [searchTerm, setSearchTerm] = useState('');
  const [search, setSearch] = useState('');
  const [city, setCity] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [novo, setNovo] = useState(false);
  const [selecionado, setSelecionado] = useState<Candidate | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchTerm.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isError } = useCandidatos({
    search: search || undefined,
    city: city || undefined,
    page: Math.floor(offset / limit) + 1,
    limit,
  });

  const candidatos = data?.data ?? [];

  return (
    <PageContainer
      title="Candidatos"
      subtitle="Cadastro, currículos e histórico de participação"
      action={
        <Can feature={FEATURES.recrutamentoCandidatos} action="create">
          <Button icon={<Plus className="h-4 w-4" />} onClick={() => setNovo(true)}>
            Novo candidato
          </Button>
        </Can>
      }
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px] flex-1">
          <TextInput
            label="Buscar por nome, CPF, e-mail ou cidade"
            icon={<Search className="h-4 w-4" />}
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.target.value);
              setOffset(0);
            }}
          />
        </div>
        <div className="w-48">
          <TextInput
            label="Cidade"
            value={city}
            onChange={(event) => {
              setCity(event.target.value);
              setOffset(0);
            }}
          />
        </div>
      </div>

      {isError && <Alert variant="error">Não foi possível carregar os candidatos.</Alert>}

      {isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : candidatos.length === 0 ? (
        <EmptyState
          title="Nenhum candidato encontrado"
          description="Cadastre um candidato ou aguarde inscrições pelo portal."
        />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Nome</th>
                <th className="px-3 py-2 text-left">CPF</th>
                <th className="px-3 py-2 text-left">E-mail</th>
                <th className="px-3 py-2 text-left">Cidade</th>
                <th className="px-3 py-2 text-right">Pretensão</th>
                <th className="px-3 py-2 text-left">Cadastro</th>
                <th className="px-3 py-2 text-left">Currículo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {candidatos.map((candidato) => (
                <tr key={candidato.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      className="font-medium text-blue-600 hover:underline"
                      onClick={() => setSelecionado(candidato)}
                    >
                      {candidato.fullName}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <span className={candidato.cpfMasked ? 'text-gray-500' : ''}>
                      {candidato.cpf}
                    </span>
                    {candidato.cpfMasked && (
                      <span
                        className="ml-1 text-xs text-gray-400"
                        title="Seu perfil não tem permissão para ver o CPF completo."
                      >
                        (mascarado)
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{candidato.email}</td>
                  <td className="px-3 py-2">{candidato.city ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    {formatCurrencyBRL(candidato.expectedSalary)}
                  </td>
                  <td className="px-3 py-2">{formatDateBR(candidato.createdAt)}</td>
                  <td className="px-3 py-2">
                    <CurriculoCell candidateId={candidato.id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <Pagination
          offset={offset}
          limit={limit}
          count={data.total}
          itemLabel="candidato"
          onChange={(next) => {
            setOffset(next.offset);
            setLimit(next.limit);
          }}
        />
      )}

      {(novo || selecionado) && (
        <CandidatoModal
          open
          candidato={selecionado}
          onClose={() => {
            setNovo(false);
            setSelecionado(null);
          }}
        />
      )}
    </PageContainer>
  );
}

/**
 * Currículo corrente: baixar ou enviar.
 *
 * O download busca a URL no clique porque ela é temporária — guardá-la
 * no estado da página entregaria um link vencido alguns minutos depois.
 */
function CurriculoCell({ candidateId }: { candidateId: string }) {
  const { data: documentos } = useDocumentosCandidato(candidateId);
  const enviar = useEnviarCurriculo();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const corrente = (documentos ?? []).find(
    (documento) => documento.type === 'curriculo' && documento.isCurrent,
  );

  async function baixar() {
    if (!corrente) return;
    const { url } = await obterUrlCurriculo(candidateId, corrente.id);
    window.open(url, '_blank', 'noopener');
  }

  return (
    <div className="flex items-center gap-1">
      {corrente ? (
        <Button
          size="sm"
          variant="ghost"
          icon={<Download className="h-3.5 w-3.5" />}
          onClick={baixar}
        >
          v{corrente.version}
        </Button>
      ) : (
        <span className="text-xs text-gray-400">sem currículo</span>
      )}

      <Can feature={FEATURES.recrutamentoCandidatos} action="edit">
        <Button
          size="sm"
          variant="ghost"
          aria-label="Enviar currículo"
          icon={<Upload className="h-3.5 w-3.5" />}
          loading={enviar.isPending}
          onClick={() => inputRef.current?.click()}
        />
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) enviar.mutate({ candidateId, file });
            event.target.value = '';
          }}
        />
      </Can>
    </div>
  );
}
